'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { fullscreenGroupIdAtom } from '@/state/layout'

interface FullscreenOverlayProps {
  groupId: string
  children: React.ReactNode
}

export function FullscreenOverlay({ groupId, children }: FullscreenOverlayProps) {
  const [fullscreenId, setFullscreenId] = useAtom(fullscreenGroupIdAtom)
  const isFullscreen = fullscreenId === groupId
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isFullscreen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A modal opened from the fullscreen widget owns the first Escape press.
        // Check during window capture, before Radix removes the dialog from the DOM.
        if (document.querySelector('[role="dialog"][data-state="open"]')) return
        setFullscreenId(null)
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown, { capture: true })

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
    }
  }, [isFullscreen, setFullscreenId])

  const overlay = (
    <AnimatePresence>
      {isFullscreen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // Below the app's overlay baseline (Popover/Select/Tooltip/Sheet all cap out
          // at z-50/z-[60]) — those are portaled straight to document.body too, so as
          // fixed-position siblings under <body> their z-index alone decides stacking,
          // regardless of DOM nesting. At z-9999 this used to sit above every dropdown
          // a fullscreen'd widget itself opens (e.g. the order form's news-type combobox),
          // silently swallowing clicks on options that were still visually on top.
          className="fixed inset-0 z-[45] flex items-center justify-center backdrop-blur-sm"
          style={{
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            background: 'var(--page-background)',
          }}
          onClick={() => setFullscreenId(null)}
        >
          <motion.div
            className="relative h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] overflow-hidden border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            // The widget grows into place and shrinks back (prototype maximize).
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.14 } }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return (
    <>
      {/* Default/Dashboard state */}
      {!isFullscreen && <motion.div className="h-full w-full">{children}</motion.div>}

      {/* Fullscreen overlay (Portal) */}
      {mounted ? createPortal(overlay, document.body) : null}
    </>
  )
}
